import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLongRightIcon } from '@heroicons/react/24/outline'
import { SystemEventName } from 'models'
import { LabelItem, LabelType } from '../../categoryDashboard.types'
import { LabelCardWrapper } from './labelsSection.styles'
import {
  LabelInstanceAddedIcon,
  LabelInstanceRemovedIcon,
  LabelSLAChangedIcon,
  LabelCPIActivatedIcon,
  LabelCustomIcon,
  LabelPublicIcon,
  LabelPrivateIcon
} from '../icons'

interface Props {
  label: LabelItem
}

const LABEL_CONFIG: Record<LabelType, { color: string; lightColor: string; icon: FC<{ className?: string }> }> = {
  [LabelType.InstanceAdded]: { color: '#CE6BCF', lightColor: '#A21CAF', icon: LabelInstanceAddedIcon },
  [LabelType.InstanceRemoved]: { color: '#E74C3C', lightColor: '#DA2D1B', icon: LabelInstanceRemovedIcon },
  [LabelType.SLAChanged]: { color: '#F09543', lightColor: '#B45309', icon: LabelSLAChangedIcon },
  [LabelType.CPIActivated]: { color: '#8E9AFF', lightColor: '#4338CA', icon: LabelCPIActivatedIcon },
  [LabelType.CustomLabel]: { color: '#FFE082', lightColor: '#976C00', icon: LabelCustomIcon }
}

const ACTION_TO_LABEL_TYPE: Record<string, LabelType> = {
  [SystemEventName.CpiInstanceAdded]: LabelType.InstanceAdded,
  [SystemEventName.CpiInstanceRemoved]: LabelType.InstanceRemoved,
  [SystemEventName.SlaChangedEvent]: LabelType.SLAChanged,
  [SystemEventName.CpiActivated]: LabelType.CPIActivated,
  [SystemEventName.CpiCustom]: LabelType.CustomLabel
}

const DEFAULT_CONFIG = { color: '#CE6BCF', lightColor: '#A21CAF', icon: LabelCustomIcon }

const LabelCard: FC<Props> = ({ label }) => {
  const navigate = useNavigate()
  const labelType = ACTION_TO_LABEL_TYPE[label.type] || label.type
  const config = LABEL_CONFIG[labelType as LabelType] || DEFAULT_CONFIG
  const Icon = config.icon

  const handleNavigate = () => {
    if (!label.cpiName) return
    navigate(`/library/cpis/${label.cpiName.toLowerCase()}/trends`)
  }

  return (
    <LabelCardWrapper labelColor={config.color} labelLightColor={config.lightColor}>
      <div className="label-header">
        <Icon className="label-icon" />
        <span className="label-title">{label.title}</span>
        <div className="cpi-chip" onClick={handleNavigate}>
          <span>{label.cpiName}</span>
        </div>
      </div>

      <div className="label-description">{label.description}</div>

      {label.slaChange && (
        <div className="label-sla-change">
          <span>{label.slaChange.from}</span>
          <ArrowLongRightIcon className="arrow-icon" />
          <span>{label.slaChange.to}</span>
        </div>
      )}

      {label.author && (
        <div className="label-author">
          <span className="author-prefix">Created by:</span>
          <span className="author-name">{label.author}</span>
        </div>
      )}

      <div className="label-footer">
        <span className="label-date">{label.date}</span>
        {label.isPublic ? (
          <LabelPublicIcon className="visibility-icon" />
        ) : (
          <LabelPrivateIcon className="visibility-icon" />
        )}
      </div>
    </LabelCardWrapper>
  )
}

export default LabelCard
